<template>
  <CxTr>
    <CxTd>{{ formattingUtils.formatDateTime(importRow.createdAt) }}</CxTd>
    <CxTd>{{ importRow.sourceName }}</CxTd>
    <CxTd>{{ importRow.destinationAccount?.name }}</CxTd>
    <CxTd>{{ contentTypeLabel }}</CxTd>
    <CxTd><ImportStatusBadge :status="importRow.status" /></CxTd>
    <CxActions
      :actions="[
        {
          icon: 'chevron_right',
          action: () => {
            $refs.importPanel.toggle();
          },
        },
      ]"
    >
    </CxActions>
    <CxPanel ref="importPanel" :title="panelTitle">
      <template #default>
        <CxRow guttersX="0" class="w-100" marginTop="md">
          <CxTitle level="3" class="mb-0">{{ translate("CATALOG_IMPORTS.GENERAL_INFORMATION") }}</CxTitle>
        </CxRow>
        <CxRow guttersX="0" class="w-100" marginTop="md" gapY="sm">
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.SOURCE") }}</CxText>
            <CxText paragraph fontSize="lg">{{ importRow.sourceName }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.DESTINATION") }}</CxText>
            <CxText paragraph fontSize="lg">{{ importRow.destinationAccount?.name }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.DATA") }}</CxText>
            <CxText paragraph fontSize="lg">{{ contentTypeLabel }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.STATUS") }}</CxText>
            <ImportStatusBadge :status="importRow.status" fontSize="lg" />
          </CxRow>
        </CxRow>
        <CxRow guttersX="0" class="w-100" marginTop="md">
          <hr class="w-100 m-0" />
        </CxRow>
        <CxRow guttersX="0" class="w-100" marginTop="md">
          <CxTitle level="3" class="mb-0">{{ translate("CATALOG_IMPORTS.DATES") }}</CxTitle>
        </CxRow>
        <CxRow guttersX="0" class="w-100" marginTop="md" gapY="sm">
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.CREATION_DATE") }}</CxText>
            <CxText paragraph fontSize="lg">{{ formattingUtils.formatDateTime(importRow.createdAt) }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between" flexDirection="row">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.QUEUED_DATE") }}</CxText>
            <CxText paragraph fontSize="lg">{{
              importRow.queuedAt ? formattingUtils.formatDateTime(importRow.queuedAt) : "-"
            }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between" flexDirection="row">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.START_DATE") }}</CxText>
            <CxText paragraph fontSize="lg">
              {{ importRow.queuedAt ? formattingUtils.formatDateTime(importRow.startedAt) : "-" }}
            </CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between" flexDirection="row">
            <CxText paragraph fontSize="lg">{{ translate("CATALOG_IMPORTS.END_DATE") }}</CxText>
            <CxText paragraph fontSize="lg">
              {{ importRow.queuedAt ? formattingUtils.formatDateTime(importRow.endedAt) : "-" }}
            </CxText>
          </CxRow>
        </CxRow>
        <CxRow guttersX="0" class="w-100" marginTop="md">
          <hr class="w-100 m-0" />
        </CxRow>
        <CxRow guttersX="0" class="w-100" marginTop="md">
          <CxTitle level="3" class="mb-0">{{ translate("CATALOG_IMPORTS.REPORT.NB_LINES") }}</CxTitle>
        </CxRow>
        <CxRow guttersX="0" class="w-100" marginTop="md" gapY="sm">
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{
              translateWithPluralization("CATALOG_IMPORTS.REPORT.LINES_FOUND", importRow.report.linesCount.found)
            }}</CxText>
            <CxText paragraph fontSize="lg">{{ importRow.report.linesCount.found }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{
              translateWithPluralization("CATALOG_IMPORTS.REPORT.LINES_INSERTED", importRow.report.linesCount.inserted)
            }}</CxText>
            <CxText paragraph fontSize="lg">{{ importRow.report.linesCount.inserted }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{
              translateWithPluralization("CATALOG_IMPORTS.REPORT.LINES_UPDATED", importRow.report.linesCount.updated)
            }}</CxText>
            <CxText paragraph fontSize="lg">{{ importRow.report.linesCount.updated }}</CxText>
          </CxRow>
          <CxRow guttersX="0" class="w-100" justifyContent="between">
            <CxText paragraph fontSize="lg">{{
              translateWithPluralization("CATALOG_IMPORTS.REPORT.LINES_IGNORED", importRow.report.linesCount.ignored)
            }}</CxText>
            <CxText paragraph fontSize="lg">{{ importRow.report.linesCount.ignored }}</CxText>
          </CxRow>
          <CxRow guttersX="0" v-if="importRow.report?.error" class="w-100">
            <CxScrollableContent>
              <CxText color="danger">{{ importRow.report.error }}</CxText>
            </CxScrollableContent>
          </CxRow>
          <CxRow guttersX="0" v-else-if="importRow.report.ignoredLines.length" class="w-100">
            <CxScrollableContent>
              <CxText paragraph v-for="(ignoredLine, index) in importRow.report.ignoredLines" :key="index">
                {{ ignoredLine }}
              </CxText>
            </CxScrollableContent>
          </CxRow>
        </CxRow>
      </template>
    </CxPanel>
  </CxTr>
</template>

<script setup lang="ts">
/* eslint no-unused-vars:off */
import formattingUtils from "@/core/utils/formatting.utils";
import { CxActions, CxTd, CxTr } from "@/designSystem/Table";
import { CxPanel } from "@/designSystem";
import { CxRow } from "@/designSystem/Grid";
import { CxText, CxTitle } from "@/designSystem/Typography";
import { computed } from "vue";
import useTranslate from "@/core/composables/translate";
import ImportStatusBadge from "@/apps/dashboard/catalog/view/Imports/ImportStatusBadge.vue";
import { Import } from "@/apps/dashboard/catalog/core/domain/Import";
import CxScrollableContent from "@/designSystem/ScrollableContainer/CxScrollableContent.vue";

const { translate, translateWithPluralization } = useTranslate();

const panelTitle = computed(() =>
  translate("CATALOG_IMPORTS.PANEL.TITLE", { date: formattingUtils.formatDateTime(importRow.createdAt) })
);

const contentTypeLabel = computed(() => {
  const label = translate(`CATALOG_IMPORTS.TYPES.${importRow.content}`);
  if (importRow.strategy) {
    return label + " (" + importRow.strategy + ")";
  }
  return label;
});

const { importRow } = defineProps({
  importRow: {
    type: Import,
    required: true,
  },
});
</script>
